'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import PhotoIcon from '@/fo/icons/base/doctor/photo.svg';
import TaplinkIcon from '@/fo/icons/base/socials/taplink.svg';
import TelegramIcon from '@/fo/icons/base/socials/tg24.svg';
import VkIcon from '@/fo/icons/base/socials/vk.svg';
import { UiArticleCarouselBase } from '@/fo/ui/article/carousel/base';
import { ArticleItem } from '@/fo/ui/article/list/base';
import { UiDoctorMapsBase } from '@/fo/ui/doctor/maps/base';
import { UiKitBreadcrumbsBase } from '@/fo/ui/kit/breadcrumbs/base';
import { UiKitPanelBase } from '@/fo/ui/kit/panel/base';
import { utilDecodeHtmlEntities } from '@/fo/util/decode-html-entities';
import { utilPluralize } from '@/fo/util/pluralize';

import { Doctor, Bother } from '../types';

import { styles } from './UiDoctorProfilePage.styles';

export interface UiDoctorProfilePageProps {
  doctor: Doctor;
  articles?: ArticleItem[] | null;
  bothers?: Bother[];
  urlData?: {
    baseUrl: string;
    currentUrl: string;
  };
}

export function UiDoctorProfilePage({
  doctor,
  articles,
  bothers = [],
  urlData
}: UiDoctorProfilePageProps) {
  const slots = styles();
  const t = useTranslations();

  const breadcrumbs = [
    { label: t('HomePage'), href: urlData ? urlData?.baseUrl : '/' },
    {
      label: t('Find a doctor'),
      href: urlData ? `${urlData?.baseUrl}/specialists` : 'specialists'
    },
    {
      label: doctor.full_name,
      href: urlData
        ? `${urlData?.baseUrl}/specialists/${doctor.url_key}`
        : `/specialists/${doctor.url_key}`
    }
  ];

  const pluralizeYears = utilPluralize({
    one: t('pluralize.years.one'),
    few: t('pluralize.years.few'),
    many: t('pluralize.years.many')
  });
  const currentYear = new Date().getFullYear();

  const getBotherLabels = (codes: string[] | null | undefined) => {
    if (!codes) return null;

    const labelsById = Object.fromEntries(
      bothers.map((item) => [item.id, item.label])
    );
    return codes.map((code) => labelsById[code] || code);
  };

  const botherLabels = getBotherLabels(doctor.bother);
  const bioHtml = utilDecodeHtmlEntities(doctor.content);

  return (
    <div className={slots.base()}>
      <UiKitBreadcrumbsBase
        items={breadcrumbs}
        className={slots.breadcrumbs()}
      />
      <div className={slots.card()}>
        <div className={slots.photo()}>
          {doctor.photo ? (
            <Image
              src={doctor.photo}
              height={196}
              width={196}
              alt={doctor.photo_alt || doctor.full_name}
            />
          ) : (
            <PhotoIcon className={slots.photoIcon()} />
          )}
        </div>
        <div className={slots.content()}>
          <div className={slots.info()}>
            <div className={slots.labels()}>
              {botherLabels?.map((label: string) => (
                <div
                  className={slots.label()}
                  key={label.trim()}
                >
                  {label.trim()}
                </div>
              ))}
            </div>
            <div className={slots.name()}>{doctor.full_name}</div>
            <p
              className={slots.subtitle()}
              dangerouslySetInnerHTML={{
                __html: doctor.regalia || ''
              }}
            />
            <div className={slots.experience()}>
              {!!doctor.qualification?.length && (
                <span>
                  {doctor.qualification?.map((item) => item).join(', ')}
                </span>
              )}
              {doctor.seniority && (
                <span>
                  {' '}
                  / {t('seniority')} {currentYear - Number(doctor.seniority)}{' '}
                  {pluralizeYears(currentYear - Number(doctor.seniority))}
                </span>
              )}
            </div>
          </div>
          <div className={slots.socials()}>
            {doctor.telegram && (
              <Link
                href={doctor.telegram}
                rel="nofollow"
              >
                <TelegramIcon />
              </Link>
            )}
            {doctor.vk && (
              <Link
                href={doctor.vk}
                rel="nofollow"
              >
                <VkIcon />
              </Link>
            )}
            {doctor.taplink && (
              <Link
                href={doctor.taplink}
                rel="nofollow"
              >
                <TaplinkIcon />
              </Link>
            )}
          </div>
        </div>
      </div>
      {doctor.clinics && doctor.clinics.length > 0 ? (
        <UiKitPanelBase
          tabsClassName={slots.tabs()}
          tabClassName={slots.tab()}
          view="tab"
          items={[
            {
              header: t('Make an appointment'),
              content: (
                <UiDoctorMapsBase
                  city={doctor.city || ''}
                  doctors={[doctor]}
                  mode="profile"
                />
              )
            },
            {
              header: t('About the doctor'),
              content: (
                <div className={slots.bio()}>
                  {bioHtml ? (
                    <>
                      <div className={slots.title()}>{t('Biography')}</div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: bioHtml
                        }}
                        className={slots.bioContent()}
                      />
                    </>
                  ) : (
                    <div className={slots.bioContent()}>
                      {t('doctor.notInfo')}
                    </div>
                  )}
                </div>
              )
            }
          ]}
        />
      ) : (
        <>
          {!!bioHtml && (
            <div className={slots.bio()}>
              <div className={slots.title()}>{t('Biography')}</div>
              <div
                dangerouslySetInnerHTML={{ __html: bioHtml }}
                className={slots.bioContent()}
              />
            </div>
          )}
        </>
      )}

      {articles && articles.length > 0 && (
        <UiArticleCarouselBase
          className={slots.articles()}
          articles={articles}
          title={t('Author of articles')}
        />
      )}
    </div>
  );
}
